import { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  User, 
  AlertTriangle, 
  PauseCircle, 
  PlayCircle,
  Search,
  ShieldAlert,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface Message {
  id: string;
  content: string;
  direction: 'incoming' | 'outgoing';
  timestamp: Date;
}

interface ChatUser {
  id: string;
  name: string;
  phone: string;
  lastMessage?: string;
  lastActivity: Date;
  unread: number;
  botPaused: boolean;
}

const Chat = () => {
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendOnEnter, setSendOnEnter] = useState(true);
  const [aiValidating, setAiValidating] = useState(false);
  const [aiValidationResult, setAiValidationResult] = useState<{show: boolean, approved: boolean, message: string} | null>(null);
  const [messageBlocked, setMessageBlocked] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Recuperar usuário logado
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isTrainee = currentUser?.role === 'trainee';

  useEffect(() => {
    // Load users from API or localStorage
    const loadUsers = async () => {
      try {
        // For demo, we'll use localStorage
        const savedUsers = localStorage.getItem('chatUsers');
        if (savedUsers) {
          const parsedUsers = JSON.parse(savedUsers);
          // Convert string dates back to Date objects
          const usersWithDates = parsedUsers.map((user: any) => ({
            ...user,
            lastActivity: new Date(user.lastActivity)
          }));
          setUsers(usersWithDates);
        } else {
          // Sample data
          const sampleUsers: ChatUser[] = [
            {
              id: '1',
              name: 'João Silva',
              phone: '5517991082156',
              lastMessage: 'Olá, estou procurando produtos para limpeza da casa',
              lastActivity: new Date(),
              unread: 2,
              botPaused: false
            },
            {
              id: '2',
              name: 'Maria Oliveira',
              phone: '5511987654321',
              lastMessage: 'Vocês têm protetor solar?',
              lastActivity: new Date(Date.now() - 3600000), // 1 hour ago
              unread: 0,
              botPaused: true
            },
            {
              id: '3',
              name: 'Carlos Pereira',
              phone: '5521912345678',
              lastMessage: 'Quero informações sobre assinaturas',
              lastActivity: new Date(Date.now() - 86400000), // 1 day ago
              unread: 1,
              botPaused: false
            }
          ];
          setUsers(sampleUsers);
          localStorage.setItem('chatUsers', JSON.stringify(sampleUsers));
        }
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    // Load messages for selected user
    if (selectedUser) {
      const loadMessages = async () => {
        try {
          // For demo, we'll use localStorage
          const savedMessages = localStorage.getItem(`messages_${selectedUser.id}`);
          if (savedMessages) {
            const parsedMessages = JSON.parse(savedMessages);
            // Convert string dates back to Date objects
            const messagesWithDates = parsedMessages.map((message: any) => ({
              ...message,
              timestamp: new Date(message.timestamp)
            }));
            setMessages(messagesWithDates);
          } else {
            // Sample messages
            const sampleMessages: Message[] = [
              {
                id: '1',
                content: 'Olá, estou procurando produtos para limpeza da casa',
                direction: 'incoming',
                timestamp: new Date(Date.now() - 3600000) // 1 hour ago
              },
              {
                id: '2',
                content: 'Olá! Sou seu assistente de compras. Como posso ajudar você hoje?\n\n🛒 Posso ajudar você a:\n• Encontrar produtos específicos\n• Criar listas de compras\n• Calcular preços e frete\n• Aplicar descontos de assinaturas\n\nQue tipo de produtos de limpeza você está procurando?',
                direction: 'outgoing',
                timestamp: new Date(Date.now() - 3540000) // 59 minutes ago
              },
              {
                id: '3',
                content: 'Preciso de produtos para limpar o banheiro',
                direction: 'incoming',
                timestamp: new Date(Date.now() - 1800000) // 30 minutes ago
              },
              {
                id: '4',
                content: 'Encontrei estes produtos de limpeza para banheiro:\n\n1. Desinfetante para Banheiro - $5.99\n2. Limpador Multiuso - $4.50\n3. Esponja Abrasiva (pacote com 3) - $3.25\n\nGostaria de adicionar algum destes produtos à sua lista de compras?',
                direction: 'outgoing',
                timestamp: new Date(Date.now() - 1740000) // 29 minutes ago
              }
            ];
            setMessages(sampleMessages);
            localStorage.setItem(`messages_${selectedUser.id}`, JSON.stringify(sampleMessages));
          }

          // Mark messages as read
          const updatedUsers = users.map(user => 
            user.id === selectedUser.id ? { ...user, unread: 0 } : user
          );
          setUsers(updatedUsers);
          localStorage.setItem('chatUsers', JSON.stringify(updatedUsers));
        } catch (error) {
          console.error('Error loading messages:', error);
        }
      };

      loadMessages();
    }
  }, [selectedUser, users]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || messageBlocked) return;

    // TODOS os atendentes devem validar mensagens com IA
    await validateMessageWithAI();
  };

  const validateMessageWithAI = async () => {
    if (!selectedUser) return;

    setAiValidating(true);
    setAiValidationResult(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      // Validar mensagem com IA
      const response = await fetch(`${apiUrl}/api/ai/validate-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      const data = await response.json();

      if (data.success) {
        const validation = data.data;

        if (validation.valid) {
          // ✅ Mensagem aprovada - enviar
          setAiValidationResult({
            show: true,
            approved: true,
            message: '✅ Mensagem aprovada pela IA'
          });

          // Usar mensagem corrigida se houver
          const messageToSend = validation.corrected_message || newMessage;
          
          setTimeout(() => {
            sendMessage(messageToSend);
            setAiValidationResult(null);
          }, 1000);
        } else {
          // ❌ Mensagem rejeitada
          const issues = validation.issues.join(', ');
          const suggestions = validation.suggestions.length > 0 
            ? '\n\nSugestões: ' + validation.suggestions.join(', ')
            : '';
          
          setAiValidationResult({
            show: true,
            approved: false,
            message: `❌ Mensagem bloqueada\n\nProblemas: ${issues}${suggestions}\n\n${validation.corrected_message ? 'Mensagem sugerida:\n' + validation.corrected_message : ''}`
          });

          // Se houver mensagem corrigida, substituir automaticamente
          if (validation.corrected_message && validation.severity !== 'high') {
            setTimeout(() => {
              setNewMessage(validation.corrected_message);
              setAiValidationResult(null);
            }, 3000);
          } else if (validation.severity === 'high') {
            // Severidade alta: bloquear e notificar supervisor
            setMessageBlocked(true);
            if (isTrainee) {
              await notifySupervisor(issues);
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao validar com IA:', error);
      // Em caso de erro, permitir envio (fallback)
      sendMessage(newMessage);
    } finally {
      setAiValidating(false);
    }
  };

  const notifySupervisor = async (feedback: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      await fetch(`${apiUrl}/api/chat/notify-supervisor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainee_id: currentUser.id,
          trainee_name: currentUser.name,
          customer_phone: selectedUser?.phone,
          customer_name: selectedUser?.name,
          blocked_message: newMessage,
          ai_feedback: feedback
        })
      });
    } catch (error) {
      console.error('Erro ao notificar supervisor:', error);
    }
  };

  const sendMessage = (messageText?: string) => {
    if (!selectedUser) return;

    const textToSend = messageText || newMessage;

    const message: Message = {
      id: Date.now().toString(),
      content: textToSend,
      direction: 'outgoing',
      timestamp: new Date()
    };

    const updatedMessages = [...messages, message];
    setMessages(updatedMessages);
    localStorage.setItem(`messages_${selectedUser.id}`, JSON.stringify(updatedMessages));
    
    // Update last message in user list
    const updatedUsers = users.map(user => 
      user.id === selectedUser.id ? { 
        ...user, 
        lastMessage: textToSend,
        lastActivity: new Date()
      } : user
    );
    setUsers(updatedUsers);
    localStorage.setItem('chatUsers', JSON.stringify(updatedUsers));

    // Send message to webhook or Evolution API
    sendMessageToWebhook(selectedUser.phone, textToSend);
    
    setNewMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const sendMessageToWebhook = async (phone: string, message: string) => {
    try {
      // Get configuration to determine which API to use
      const configStr = localStorage.getItem('configurations');
      const config = configStr ? JSON.parse(configStr) : {};
      
      if (config.evolution_api_enabled && config.evolution_api_url) {
        // Send to Evolution API
        console.log('Sending to Evolution API:', {
          number: phone,
          textMessage: { text: message }
        });
        
        // In a real implementation, this would be an actual API call
        // await fetch(`${config.evolution_api_url}/message/sendText/${config.evolution_instance}`, {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     number: phone,
        //     textMessage: { text: message }
        //   })
        // });
      } else if (config.webhook_enabled && config.webhook_url) {
        // Send to webhook
        console.log('Sending to webhook:', {
          phone: phone,
          message: message,
          direction: 'outgoing'
        });
        
        // In a real implementation, this would be an actual API call
        // await fetch(config.webhook_url, {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     phone: phone,
        //     message: message,
        //     direction: 'outgoing'
        //   })
        // });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const toggleBotPause = (userId: string) => {
    const updatedUsers = users.map(user => 
      user.id === userId ? { ...user, botPaused: !user.botPaused } : user
    );
    setUsers(updatedUsers);
    localStorage.setItem('chatUsers', JSON.stringify(updatedUsers));
    
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser({ ...selectedUser, botPaused: !selectedUser.botPaused });
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-9rem)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Chat</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar usuários..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Users List */}
        <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Conversas</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Nenhum usuário encontrado
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div 
                  key={user.id} 
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedUser?.id === user.id ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center">
                          <h3 className="font-medium text-gray-900">{user.name}</h3>
                          {user.unread > 0 && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                              {user.unread}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{user.phone}</p>
                        {user.lastMessage && (
                          <p className="text-sm text-gray-600 mt-1 truncate max-w-[200px]">
                            {user.lastMessage}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-xs text-gray-500">
                        {formatDate(user.lastActivity)}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBotPause(user.id);
                        }}
                        className={`mt-1 p-1 rounded-full ${
                          user.botPaused 
                            ? 'text-green-600 hover:bg-green-100' 
                            : 'text-red-600 hover:bg-red-100'
                        }`}
                        title={user.botPaused ? 'Ativar Bot' : 'Pausar Bot'}
                      >
                        {user.botPaused ? (
                          <PlayCircle className="h-5 w-5" />
                        ) : (
                          <PauseCircle className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="font-medium text-gray-900">{selectedUser.name}</h3>
                    <p className="text-sm text-gray-500">{selectedUser.phone}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleBotPause(selectedUser.id)}
                  className={`flex items-center px-3 py-1 rounded-lg ${
                    selectedUser.botPaused 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {selectedUser.botPaused ? (
                    <>
                      <PlayCircle className="h-4 w-4 mr-1" />
                      Ativar Bot
                    </>
                  ) : (
                    <>
                      <PauseCircle className="h-4 w-4 mr-1" />
                      Pausar Bot
                    </>
                  )}
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <MessageSquare className="h-12 w-12 text-gray-300 mb-2" />
                    <p>Nenhuma mensagem ainda</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.direction === 'incoming' ? 'justify-start' : 'justify-end'
                        }`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                            message.direction === 'incoming'
                              ? 'bg-gray-100 text-gray-900'
                              : 'bg-blue-600 text-white'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          <p
                            className={`text-xs mt-1 text-right ${
                              message.direction === 'incoming'
                                ? 'text-gray-500'
                                : 'text-blue-100'
                            }`}
                          >
                            {formatTimestamp(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                {/* Validação IA */}
                {aiValidationResult?.show && (
                  <div className={`mb-3 p-3 rounded-lg flex items-start ${
                    aiValidationResult.approved ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    {aiValidationResult.approved ? (
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${
                        aiValidationResult.approved ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {aiValidationResult.approved ? '✅ Resposta Aprovada' : '❌ Resposta Reprovada'}
                      </p>
                      <p className={`text-xs mt-1 ${
                        aiValidationResult.approved ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {aiValidationResult.message}
                      </p>
                    </div>
                  </div>
                )}

                {/* Mensagem bloqueada */}
                {messageBlocked && (
                  <div className="mb-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    <div className="flex items-start">
                      <ShieldAlert className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-yellow-800">🔒 Atendimento Bloqueado</p>
                        <p className="text-xs mt-1 text-yellow-700">
                          Sua resposta foi reprovada pela IA. Um supervisor foi notificado e precisa liberar o atendimento.
                        </p>
                        <button
                          onClick={() => {
                            setMessageBlocked(false);
                            setAiValidationResult(null);
                            setNewMessage('');
                          }}
                          className="mt-2 text-xs text-yellow-800 underline hover:text-yellow-900"
                        >
                          Limpar e tentar novamente
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      // Auto-resize
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder={messageBlocked ? 'Atendimento bloqueado...' : 'Digite sua mensagem...'}
                    disabled={messageBlocked || aiValidating}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[42px] max-h-[200px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && sendOnEnter) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || messageBlocked || aiValidating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed h-[42px] flex items-center justify-center"
                  >
                    {aiValidating ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Opções */}
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendOnEnter}
                        onChange={(e) => setSendOnEnter(e.target.checked)}
                        className="mr-2"
                      />
                      Enviar com Enter
                    </label>
                    {isTrainee && (
                      <div className="flex items-center text-xs text-orange-600">
                        <ShieldAlert className="h-3 w-3 mr-1" />
                        Modo Trainee - IA validará suas respostas
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {sendOnEnter ? 'Shift+Enter para quebra de linha' : 'Enter para quebra de linha'}
                  </p>
                </div>

                {selectedUser.botPaused && (
                  <div className="mt-2 text-xs text-red-600 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Bot está pausado. As mensagens serão enviadas diretamente sem processamento da IA.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MessageSquare className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Nenhuma conversa selecionada</h3>
              <p>Selecione um usuário para ver a conversa</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;